import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Mail } from "lucide-react";

const FlagHU = () => (
  <svg viewBox="0 0 24 16" className="w-4 h-3 rounded-[2px] shrink-0" aria-hidden="true">
    <rect width="24" height="16" fill="#CD2A3E" />
    <rect y="5.33" width="24" height="5.33" fill="#FFFFFF" />
    <rect y="10.66" width="24" height="5.34" fill="#436F4D" />
  </svg>
);

const FlagUS = () => (
  <svg viewBox="0 0 24 16" className="w-4 h-3 rounded-[2px] shrink-0" aria-hidden="true">
    <rect width="24" height="16" fill="#B22234" />
    {[1, 3, 5, 7, 9, 11].map((i) => (
      <rect key={i} y={(i * 16) / 13} width="24" height={16 / 13} fill="#FFFFFF" />
    ))}
    <rect width="10" height={(16 * 7) / 13} fill="#3C3B6E" />
  </svg>
);
import ConsultModal from "@/components/ConsultModal";

export function LanguageToggle() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-border bg-white p-0.5" data-testid="language-toggle">
      {["en", "hu"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
            lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {l === "hu" ? <FlagHU /> : <FlagUS />}
          {l}
        </button>
      ))}
    </div>
  );
}

export default function SiteNav({ landing = false }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [consultOpen, setConsultOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const links = landing
    ? [
        ["#about", t("nav.about")],
        ["#specialties", t("nav.specialties")],
        ["#levels", t("nav.levels")],
        ["#pricing", t("nav.pricing")],
        ["#results", t("nav.results")],
        ["#how", t("nav.faq")],
      ]
    : [];

  const goHome = (e) => {
    e.preventDefault();
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-md border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" onClick={goHome} className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <img src="/logo.png" alt="English Universe Stars" className="h-14 w-auto" />
          <span className="font-serif font-bold text-lg leading-none hidden lg:block" data-testid="nav-brand-text">English Universe Stars</span>
        </Link>
        <div className="flex items-center shrink-0" data-testid="nav-contact-icons">
          <a href="tel:+16466103376" className="p-2 rounded-full text-primary hover:bg-primary/10 transition-colors" data-testid="nav-call-icon" aria-label="Call 646-610-3376" title="646-610-3376">
            <Phone className="w-5 h-5" />
          </a>
          <a href="mailto:englishuniversestars@yahoo.com" className="p-2 rounded-full text-primary hover:bg-primary/10 transition-colors" data-testid="nav-email-icon" aria-label="Email englishuniversestars@yahoo.com" title="englishuniversestars@yahoo.com">
            <Mail className="w-5 h-5" />
          </a>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle />
          {user ? (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")} data-testid="nav-dashboard-btn">
                {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout-btn">{t("nav.logout")}</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="hidden sm:inline-flex" data-testid="nav-login-btn">
                {t("nav.login")}
              </Button>
              <Button size="sm" onClick={() => setConsultOpen(true)} className="hidden sm:inline-flex" data-testid="nav-book-consult-btn">
                {t("nav.book")}
              </Button>
            </>
          )}
          <button className="p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-btn" aria-label="Menu">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-border bg-background px-4 py-4 flex flex-col gap-3" data-testid="nav-mobile-menu">
          {links.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)} className="text-sm font-semibold text-muted-foreground">
              {label}
            </a>
          ))}
          {!user && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="nav-mobile-login-btn">{t("nav.login")}</Button>
              <Button size="sm" onClick={() => { setMenuOpen(false); setConsultOpen(true); }} data-testid="nav-mobile-book-btn">{t("nav.book")}</Button>
            </>
          )}
        </div>
      )}

      <ConsultModal open={consultOpen} onOpenChange={setConsultOpen} />
    </header>
  );
}
